// =============================================================================
// Phase 3: Expression Parser
// Parses Grafana-style time expressions like "now-7d", "now/M", "now-1d/d+6h"
// =============================================================================

export interface Operation {
  type: "subtract" | "add" | "round";
  value?: number;
  unit: string;
}

export interface ExpressionResult {
  isExpression: boolean;
  anchor: string | null;
  operations: Operation[];
  error?: string;
}

// Valid time units (case-sensitive)
// Lowercase: s=seconds, m=minutes, h=hours, d=days, w=weeks, y=years
// Uppercase: M=months
const VALID_UNITS = new Set(["s", "m", "h", "d", "w", "M", "y"]);

// Units that are valid for rounding
const ROUND_UNITS = new Set(["s", "m", "h", "d", "w", "M", "y"]);

/**
 * Parse ISO 8601 duration format
 * Format: P[n]Y[n]M[n]DT[n]H[n]M[n]S or P[n]W
 * Examples: P7D (7 days), PT2H (2 hours), P1Y2M3DT4H5M6S, P2W (2 weeks)
 * Returns array of operations or null if not a valid duration
 */
export function parseISO8601Duration(input: string): Operation[] | null {
  // Must start with P (period)
  if (!input.startsWith("P") && !input.startsWith("p")) {
    return null;
  }

  const upper = input.toUpperCase();

  // Handle P[n]W format (weeks only, cannot be combined with other units)
  const weekMatch = upper.match(/^P(\d+)W$/);
  if (weekMatch) {
    return [{ type: "add", value: parseInt(weekMatch[1], 10), unit: "w" }];
  }

  // Parse P[n]Y[n]M[n]DT[n]H[n]M[n]S format
  const operations: Operation[] = [];

  // Split by T to separate date and time parts
  const parts = upper.slice(1).split("T");
  const datePart = parts[0] || "";
  const timePart = parts[1] || "";

  // Parse date part (Y, M, D)
  if (datePart) {
    // Match year
    const yearMatch = datePart.match(/(\d+)Y/);
    if (yearMatch) {
      operations.push({ type: "add", value: parseInt(yearMatch[1], 10), unit: "y" });
    }

    // Match month (M in date part)
    const monthMatch = datePart.match(/(\d+)M/);
    if (monthMatch) {
      operations.push({ type: "add", value: parseInt(monthMatch[1], 10), unit: "M" });
    }

    // Match day
    const dayMatch = datePart.match(/(\d+)D/);
    if (dayMatch) {
      operations.push({ type: "add", value: parseInt(dayMatch[1], 10), unit: "d" });
    }
  }

  // Parse time part (H, M, S)
  if (timePart) {
    // Match hour
    const hourMatch = timePart.match(/(\d+)H/);
    if (hourMatch) {
      operations.push({ type: "add", value: parseInt(hourMatch[1], 10), unit: "h" });
    }

    // Match minute (M in time part)
    const minuteMatch = timePart.match(/(\d+)M/);
    if (minuteMatch) {
      operations.push({ type: "add", value: parseInt(minuteMatch[1], 10), unit: "m" });
    }

    // Match second
    const secondMatch = timePart.match(/(\d+)S/);
    if (secondMatch) {
      operations.push({ type: "add", value: parseInt(secondMatch[1], 10), unit: "s" });
    }
  }

  // If we found no operations but input had more than just "P", it's invalid
  if (operations.length === 0 && upper.length > 1) {
    // Check if it's just "PT" or has invalid format
    if (upper === "P" || upper === "PT") {
      return null;
    }
    // Has content but we couldn't parse anything valid
    return null;
  }

  // Empty duration (just "P") is invalid
  if (operations.length === 0) {
    return null;
  }

  return operations;
}

// Normalize unit to correct case
// Lowercase: s, m, h, d, w, y
// Uppercase: M (months)
// Note: 'm' stays as 'm' (minutes), 'M' stays as 'M' (months)
function normalizeUnit(unit: string): string {
  // M stays as M (months) - lowercase m is minutes
  if (unit === "M") {
    return "M";
  }
  // Everything else is lowercase
  return unit.toLowerCase();
}

/**
 * Parse a Grafana-style time expression
 * Examples: "now", "now-7d", "now/M", "now-1d/d+6h", "-7d", "/d"
 */
export function parseExpression(input: string): ExpressionResult {
  const trimmed = input.trim();

  // Handle empty input
  if (!trimmed) {
    return {
      isExpression: false,
      anchor: null,
      operations: [],
    };
  }

  // Check if this looks like an expression
  // Must start with "now", "-", "+", or "/"
  const lowerInput = trimmed.toLowerCase();

  // Check for multiple "now" keywords - this is invalid
  // (e.g., "now-2d-5d - now-2d" or "now-1d/now" are malformed)
  const nowCount = (lowerInput.match(/\bnow\b/g) || []).length;
  if (nowCount > 1) {
    throw new Error('Invalid expression: contains multiple "now" keywords. Use "X to Y" format for ranges.');
  }

  // Check for non-expression patterns first
  if (isNotAnExpression(trimmed, lowerInput)) {
    return {
      isExpression: false,
      anchor: null,
      operations: [],
    };
  }

  let anchor = "now";
  let rest = trimmed;

  // Handle explicit anchors: "now", "start", "end"
  // Note: We trim rest to handle whitespace between anchor and operations (e.g., "start + 5h")
  if (lowerInput.startsWith("now")) {
    anchor = "now";
    rest = trimmed.slice(3).trimStart();
  } else if (lowerInput.startsWith("start")) {
    anchor = "start";
    rest = trimmed.slice(5).trimStart();
  } else if (lowerInput.startsWith("end")) {
    anchor = "end";
    rest = trimmed.slice(3).trimStart();
  } else if (trimmed.startsWith("-") || trimmed.startsWith("+") || trimmed.startsWith("/")) {
    // Implicit "now" - starts with operator
    anchor = "now";
    rest = trimmed;
  } else {
    // Check for ISO date anchor (e.g., "2026-01-15-7d", "2026-01-15+1d")
    const isoAnchor = extractISODateAnchor(trimmed);
    if (isoAnchor) {
      anchor = isoAnchor.anchor;
      rest = isoAnchor.rest;
    } else {
      // Check for invalid anchor (like "nw-5m" or "no-5m")
      const anchorMatch = trimmed.match(/^([a-zA-Z]+)/);
      if (anchorMatch) {
        const possibleAnchor = anchorMatch[1].toLowerCase();
        if (possibleAnchor !== "now" && possibleAnchor !== "start" && possibleAnchor !== "end") {
          // Truly invalid - return a result that indicates it's defined but not a valid expression
          return {
            isExpression: false,
            anchor: null,
            operations: [],
          };
        }
      } else {
        return {
          isExpression: false,
          anchor: null,
          operations: [],
        };
      }
    }
  }

  // If nothing after anchor, return plain "now" (but not for start/end which require operations)
  if (!rest) {
    // start/end anchors must have at least one operation
    if (anchor === "start" || anchor === "end") {
      return {
        isExpression: false,
        anchor: null,
        operations: [],
      };
    }
    return {
      isExpression: true,
      anchor,
      operations: [],
    };
  }

  // Parse operations
  const operations: Operation[] = [];
  let pos = 0;

  while (pos < rest.length) {
    const char = rest[pos];

    if (char === "-" || char === "+") {
      // Subtract or add operation
      const isSubtract = char === "-";
      pos++;

      // Check for invalid sequences like "--", "++", "+-", "-+", "-/", "+/"
      if (pos < rest.length) {
        const nextChar = rest[pos];
        if (nextChar === "-" || nextChar === "+" || nextChar === "/") {
          throw new Error(`Invalid operator sequence at position ${pos}`);
        }
      }

      // Check for incomplete expression (ends with operator)
      if (pos >= rest.length) {
        throw new Error("Incomplete expression: missing value or unit after operator");
      }

      // Check for ISO 8601 duration (starts with P)
      const afterOperator = rest.slice(pos);
      if (afterOperator.toUpperCase().startsWith("P")) {
        // Try to extract the duration part (up to next operator or end)
        const durationMatch = afterOperator.match(/^(P[^+\-/]*)/i);
        if (durationMatch) {
          const durationStr = durationMatch[1];
          const durationOps = parseISO8601Duration(durationStr);
          if (durationOps && durationOps.length > 0) {
            // Convert all operations to the correct type (subtract if needed)
            for (const op of durationOps) {
              operations.push({
                type: isSubtract ? "subtract" : "add",
                value: op.value,
                unit: op.unit,
              });
            }
            pos += durationStr.length;
            continue;
          }
        }
      }

      // Skip leading whitespace after operator
      while (pos < rest.length && /\s/.test(rest[pos])) {
        pos++;
      }

      // Parse number (optional, defaults to 1)
      let value = 1;
      const numMatch = rest.slice(pos).match(/^(\d+)/);
      if (numMatch) {
        value = parseInt(numMatch[1], 10);
        pos += numMatch[1].length;
      }

      // Skip whitespace between number and unit
      while (pos < rest.length && /\s/.test(rest[pos])) {
        pos++;
      }

      // Check for decimal (not supported)
      if (rest[pos] === ".") {
        throw new Error("Decimal numbers are not supported");
      }

      // Check for incomplete expression (ends with number, no unit)
      if (pos >= rest.length) {
        throw new Error("Incomplete expression: missing unit after value");
      }

      // Parse unit and normalize case
      const rawUnit = rest[pos];
      const unit = normalizeUnit(rawUnit);

      // Validate unit
      if (!VALID_UNITS.has(unit)) {
        throw new Error(`Invalid unit '${rawUnit}'`);
      }

      operations.push({
        type: isSubtract ? "subtract" : "add",
        value,
        unit,
      });

      pos++;
    } else if (char === "/") {
      // Round operation
      pos++;

      // Check for invalid sequences like "//"
      if (pos < rest.length && rest[pos] === "/") {
        throw new Error("Invalid operator sequence: double slash");
      }

      // Check for incomplete expression
      if (pos >= rest.length) {
        throw new Error("Missing round unit after /");
      }

      // Parse unit and normalize case
      const rawUnit = rest[pos];
      const unit = normalizeUnit(rawUnit);

      // Validate unit
      if (!ROUND_UNITS.has(unit)) {
        throw new Error(`Invalid round unit '${rawUnit}'`);
      }

      operations.push({
        type: "round",
        unit,
      });

      pos++;
    } else if (/\s/.test(char)) {
      // Skip whitespace
      pos++;
    } else {
      // Invalid character
      throw new Error(`Invalid character '${char}' at position ${pos}`);
    }
  }

  // Validate: start/end anchors must have at least one operation
  // (they can't be used alone like "now" can)
  if ((anchor === "start" || anchor === "end") && operations.length === 0) {
    return {
      isExpression: false,
      anchor: null,
      operations: [],
    };
  }

  return {
    isExpression: true,
    anchor,
    operations,
  };
}

/**
 * Check if input is definitely NOT an expression
 */
function isNotAnExpression(input: string, lowerInput: string): boolean {
  // Bare number with unit but no operator (e.g., "5m", "7d")
  if (/^\d+[a-zA-Z]+$/.test(input)) {
    return true;
  }

  // Date keywords (but NOT start/end which are valid anchors)
  const keywords = ["today", "yesterday", "tomorrow", "last week", "last month"];
  if (keywords.some(k => lowerInput === k || lowerInput.startsWith(k + " "))) {
    return true;
  }

  // Month names (jan, january, feb, etc.)
  const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
  if (months.some(m => lowerInput.startsWith(m))) {
    return true;
  }

  // Plain ISO date without operations (YYYY-MM-DD only, no operators after)
  // If it has operations like "2026-01-15-7d" or "2026-01-15+1d", it IS an expression
  if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
    return true;
  }

  return false;
}

/**
 * Extract ISO date anchor from expression like "2026-01-15-7d" or "2026-01-15+1d"
 * Returns the ISO date string and the remaining operations part
 */
function extractISODateAnchor(input: string): { anchor: string; rest: string } | null {
  // Match: YYYY-MM-DD followed by an operator (-/+/)
  const match = input.match(/^(\d{4}-\d{2}-\d{2})([+\-/].*)$/);
  if (match) {
    return { anchor: match[1], rest: match[2] };
  }
  return null;
}
